import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import uniqBy from "lodash/uniqBy";
import { REQUEST_LOADING_STATES } from "../../helpers/constant";
import { IEditCreatorChatDefaultPageRequest, IEditCreatorPersonalInfosRequest, IEditPersonalInfosRequest } from "../../helpers/requests";
import { IBankingSetting, IUser, IUserState } from "../../helpers/types";
import { liveService, userService } from "../../services";

const initialState: IUserState = {
  otherUsers: [],
  currentUser: null,
  currentUserSettings: null,
  list: [],
  recentList: null,
  item: null,
  currentUserProfile: null,
  loading: REQUEST_LOADING_STATES.IDLE,
  error: null,
  total: 0,
  currentCategory: "",
  action: "",
};

export const getUserInfos = createAsyncThunk("users/getUserInfos", async (includePrivateInfos: boolean = false) => {
  return userService.getUserInfos(includePrivateInfos);
});

export const getUserSettings = createAsyncThunk("users/getUserSettings", async () => {
  return userService.getUserSettings();
});

export const getOtherUserInfos = createAsyncThunk("users/getOtherUserInfos", async (idOrUsername: string) => {
  return userService.getOtherUserInfos(idOrUsername);
});

export const getUserBanks = createAsyncThunk("users/getUserBanks", async () => {
  return userService.getCreatorBanks();
});

export const newUserBank = createAsyncThunk("users/newUserBank", async (payload: IBankingSetting) => {
  return userService.createBankingSetting(payload);
});

export const fetchUserScheduledEvent = createAsyncThunk(
  "lives/fetchUserScheduledEvent",
  async ({ id, isCurrentUser }: { id: string; isCurrentUser: boolean }) => {
    const event = await liveService.getCreatorScheduleLive(id);
    return { event, isCurrentUser };
  }
);

export const updateTutorialVisibilityStatus = createAsyncThunk("users/updateTutorialVisibilityStatus", async (modalKey: string) => {
  return userService.updateTutorialVisibilityStatus(modalKey);
});

export const updateUserSettingsStatus = createAsyncThunk("users/updateUserSettingsStatus", async (fieldName: string) => {
  return userService.updateUserSettingsStatus(fieldName);
});

export const updatePhoneModalShown = createAsyncThunk("users/updatePhoneModalShown", async () => {
  return userService.updatePhoneModalShown();
});

export const updateUserSettingsDefaultPageAndChat = createAsyncThunk(
  "users/updateUserSettingsDefaultPageAndChat",
  async (payload: IEditCreatorChatDefaultPageRequest) => {
    return userService.updateUserSettingsDefaultPageAndChat(payload);
  }
);

export const getMyProfile = createAsyncThunk("users/getMyProfile", async () => {
  return userService.getMyProfile();
});

export const getProfileById = createAsyncThunk("users/getProfileById", async (id: string) => {
  return userService.getProfile(id);
});

export const search = createAsyncThunk("users/search", async (param: any) => {
  return userService.search(param);
});

export const searchMoreUsers = createAsyncThunk("users/searchMoreUsers", async (param: any) => {
  return userService.search(param);
});
export const updateCoverImage = createAsyncThunk("users/updateCoverImage", async (data: FormData) => {
  return userService.updateCoverImage(data);
});

export const updateAvatar = createAsyncThunk("users/updateAvatar", async (data: FormData) => {
  return userService.updateAvatarImage(data);
});

export const updateProfile = createAsyncThunk("users/updateProfile", async (user: IUser) => {
  return userService.updateMyProfile(user);
});

export const updateUserInfos = createAsyncThunk(
  "users/updateUserInfos",
  async (payload: IEditPersonalInfosRequest | IEditCreatorPersonalInfosRequest) => {
    return userService.updateUserInfos(payload);
  }
);

export const getRecentSearch = createAsyncThunk("users/recentSearch", (seeAll?: boolean) => {
  return userService.getRecentSearch(seeAll ?? false);
});

export const addRecentSeacrchUser = createAsyncThunk("user/createRecetSearch", (id: String) => {
  return userService.addRecentSeacrchUser(id);
});

export const deleteRecentSerachUser = createAsyncThunk("user/deleteRecentSearch", (id: String) => {
  return userService.deleteRecetSearchUser(id);
});

export const getPublicProfileByUsername = createAsyncThunk("users/getProfileByUsername", async (username: string) => {
  return userService.getPublicProfile(username);
});

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    editProfile: (state, action) => {
      state.currentUserProfile = action.payload;
    },
    resetList: (state) => {
      state.list = [];
    },
    setCurrentCategory: (state, action) => {
      state.currentCategory = action.payload;
    },

    userHasExpiredDiscover: (state, action) => {
      if (state.currentUserSettings !== null) {
        if (state.currentUserSettings.expiredDiscovers !== action.payload) {
          state.currentUserSettings.expiredDiscovers = action.payload;
        }
      }
    },
    userHasRejectedDiscover: (state, action) => {
      if (state.currentUserSettings !== null) {
        if (state.currentUserSettings.rejectedDiscovers !== action.payload) {
          state.currentUserSettings.rejectedDiscovers = action.payload;
        }
      }
    },
    updateCreatorHasDiscovers: (state, action) => {
      if (state.currentUserSettings !== null) {
        if (state.currentUserSettings.hasDiscovers !== action.payload) {
          state.currentUserSettings.hasDiscovers = action.payload;
        }
      }
    },

    updateBillingAddressInfos: (state, action) => {
      if (state.currentUser !== null) {
        state.currentUser.infos = action.payload;
      }
    },

    updateToDefaultAvatar: (state, action) => {
      const currentUser = state.currentUser;
      if (currentUser) {
        state.currentUser = {
          ...currentUser,
          infos: {
            ...currentUser.infos,
            avatar: action.payload,
          },
          lft: new Date().getTime(),
        };
      }
    },
    updateDefaultBankAccount: (state, action) => {
      const currentUser = state.currentUser;
      if (currentUser) {
        const banks = currentUser.infos.banks.map((bank) => {
          if (bank._id === action.payload) {
            return { ...bank, byDefault: true };
          }
          return { ...bank, byDefault: false };
        });
        state.currentUser = {
          ...currentUser,
          infos: {
            ...currentUser.infos,
            banks,
          },
        };
      }
    },
    removeBankAccount: (state, action) => {
      const currentUser = state.currentUser;
      if (currentUser) {
        const banks = currentUser.infos.banks.filter((bank) => bank._id !== action.payload);
        state.currentUser = {
          ...currentUser,
          infos: {
            ...currentUser.infos,
            banks,
          },
        };
      }
    },
    updatePayoutFrequency: (state, action) => {
      const currentUser = state.currentUser;
      if (currentUser) {
        const privateInfos = currentUser.infos.privateInfos;
        if (privateInfos) {
          privateInfos.paymentFrequency = action.payload.paymentFrequency;
          privateInfos.payout_status = action.payload.payout_status;
          privateInfos.frequencyType = action.payload.frequencyType;
        }
        state.currentUser = {
          ...currentUser,
          infos: {
            ...currentUser.infos,
            privateInfos,
          },
        };
      }
    },
    updateUserReaction: (state, action) => {
      const otherUsers = state.otherUsers;
      const user = otherUsers.findIndex((user) => user.id === action.payload.id);
      if (user !== -1) {
        otherUsers[user] = {
          ...otherUsers[user],
          infos: {
            ...otherUsers[user].infos,
            isFollowed: action.payload.isFollowed,
          },
        };
        state.otherUsers = otherUsers;
      }
    },
    updateUserSubscription: (state, action) => {
      const otherUsers = state.otherUsers;
      const user = otherUsers.findIndex((user) => user.id === action.payload.id);
      if (user !== -1) {
        otherUsers[user] = {
          ...otherUsers[user],
          infos: {
            ...otherUsers[user].infos,
            isSubscribed: true,
          },
        };
        state.otherUsers = otherUsers;
      }
    },
    clearLAEData: (state) => {
      state.action = "";
      state.loading = REQUEST_LOADING_STATES.IDLE;
      state.error = null;
    },
    updateUserSettings: (state, action) => {
      if (state.currentUserSettings) {
        state.currentUserSettings = action.payload;
      }
    },
    deleteUserCurrentUserEvent: (state) => {
      if (state.currentUser) {
        state.currentUser.event = null;
      }
    },
    updateOtherUserEventPayment: (state, action) => {
      const otherUsers = state.otherUsers;
      const user = otherUsers.findIndex((user) => user.id === action.payload.id);
      if (user !== -1 && otherUsers[user].event) {
        otherUsers[user] = {
          ...otherUsers[user],
          event: {
            ...otherUsers[user].event,
            isPaymentVerified: true,
          },
        };
        state.otherUsers = otherUsers;
      }
    },
    clearOtherUserInfoData: (state) => {
      state.otherUsers = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserInfos.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(getUserInfos.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.currentUser = {
        infos: action.payload,
        id: action.payload._id,
        ttl: 5,
        lft: new Date().getTime(),
        event: null,
      };
      state.error = null;
    });
    builder.addCase(getUserInfos.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getUserSettings.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(getUserSettings.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.currentUserSettings = action.payload;
      state.error = null;
    });
    builder.addCase(getUserSettings.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateTutorialVisibilityStatus.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(updateTutorialVisibilityStatus.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      if (state.currentUserSettings) {
        state.currentUserSettings = {
          ...state.currentUserSettings,
          visibilityStatus: action.payload.visibilityStatus,
        };
      } else {
        //NOTE: I ADDED THIS TO PREVENT THE ERROR WHEN THE USER RELOAD ON THE PROFILE PAGE => WILL NOT CLOSE THE LEARNING COMPONENT
        //TODO: REMOVE IT AFTER IMPLEMENTING STORE CACHE
        state.currentUserSettings = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateTutorialVisibilityStatus.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateUserSettingsStatus.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(updateUserSettingsStatus.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      if (state.currentUserSettings) {
        state.currentUserSettings = {
          ...state.currentUserSettings,
          ...action.payload,
        };
      }
      state.error = null;
    });
    builder.addCase(updateUserSettingsStatus.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updatePhoneModalShown.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });

    builder.addCase(updatePhoneModalShown.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      if (state.currentUserSettings) {
        state.currentUserSettings = {
          ...state.currentUserSettings,
          phoneModalShown: action.payload.phoneModalShown,
        };
      }
      state.error = null;
    });

    builder.addCase(updatePhoneModalShown.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateUserSettingsDefaultPageAndChat.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(updateUserSettingsDefaultPageAndChat.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      if (state.currentUserSettings) {
        state.currentUserSettings = {
          ...state.currentUserSettings,
          defaultPage: action.payload.defaultPage,
          chat: action.payload.chat,
          isFreeSubscription: action.payload.isFreeSubscription,
        };
      }
      state.error = null;
    });
    builder.addCase(updateUserSettingsDefaultPageAndChat.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getOtherUserInfos.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(getOtherUserInfos.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      const otherUsers = state.otherUsers;
      if (otherUsers) {
        const user = otherUsers.findIndex((user) => user.id === action.payload.user._id);
        if (user !== -1) {
          otherUsers[user] = {
            ...otherUsers[user],
            infos: action.payload.user,
            userSettings: action.payload.userSettings,
          };
          state.otherUsers = otherUsers;
        } else {
          state.otherUsers = [
            ...otherUsers,
            {
              id: action.payload.user._id,
              infos: action.payload.user,
              userSettings: action.payload.userSettings,
              ttl: 5,
              lft: new Date().getTime(),
              event: null,
            },
          ];
        }
      }
      state.error = null;
    });
    builder.addCase(getOtherUserInfos.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateUserInfos.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(updateUserInfos.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
      const currentUser = state.currentUser;
      if (currentUser) {
        const subscribers = state.currentUser?.infos?.stats?.subscribers || 0;
        const newInfos = {
          ...action.payload,
          stats: {
            ...action.payload.stats,
            subscribers,
          },
        };
        state.currentUser = {
          ...currentUser,
          infos: newInfos,
          lft: new Date().getTime(),
        };
      }
      state.action = "updateProfile";
    });
    builder.addCase(updateUserInfos.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.action = "updateProfile";
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getUserBanks.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(getUserBanks.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
      const currentUser = state.currentUser;
      if (currentUser) {
        state.currentUser = {
          ...currentUser,
          infos: {
            ...currentUser.infos,
            banks: action.payload,
          },
        };
      }
    });
    builder.addCase(getUserBanks.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(newUserBank.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(newUserBank.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
      const currentUser = state.currentUser;
      if (currentUser) {
        state.currentUser = {
          ...currentUser,
          infos: {
            ...currentUser.infos,
            banks: [action.payload, ...currentUser.infos.banks],
          },
        };
      }
      state.action = "newUserBank";
    });
    builder.addCase(newUserBank.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.action = "newUserBank";
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(fetchUserScheduledEvent.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(fetchUserScheduledEvent.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      if (action.payload.event) {
        const scheduledEvent = action.payload.event;
        if (action.payload.isCurrentUser) {
          if (state.currentUser) {
            state.currentUser = {
              ...state.currentUser,
              event: scheduledEvent,
            };
          }
        } else {
          state.otherUsers = state.otherUsers.map((user) => {
            if (user.id === scheduledEvent.creator?._id) {
              return {
                ...user,
                event: scheduledEvent,
              };
            }
            return user;
          });
        }
      }
      state.error = null;
    });
    builder.addCase(fetchUserScheduledEvent.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(search.fulfilled, (state, action) => {
      if (state.total !== action.payload.total) {
        state.list = [...action.payload.data];
      } else {
        state.list = uniqBy([...state.list, ...action.payload.data], "_id");
      }
      state.total = action.payload.total;
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
    });

    builder.addCase(searchMoreUsers.fulfilled, (state, action) => {
      state.list = uniqBy([...state.list, ...action.payload.data], "_id");
      state.total = action.payload.total;
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
    });

    builder.addCase(getRecentSearch.fulfilled, (state, action) => {
      state.recentList = action.payload;
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
    });

    builder.addCase(addRecentSeacrchUser.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
    });

    builder.addCase(deleteRecentSerachUser.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
    });

    builder.addMatcher(isAnyOf(getMyProfile.fulfilled), (state, action) => {
      state.currentUserProfile = action.payload;
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
    });

    builder.addMatcher(isAnyOf(getMyProfile.pending), (state, action) => {
      state.currentUserProfile = null;
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addMatcher(isAnyOf(getMyProfile.rejected), (state, action) => {
      state.currentUserProfile = null;
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something Went Wrong";
    });

    builder.addMatcher(isAnyOf(getProfileById.fulfilled), (state, action) => {
      state.item = action.payload;
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
    });

    builder.addMatcher(isAnyOf(search.pending), (state, action) => {
      state.list = [];
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addMatcher(isAnyOf(search.rejected, searchMoreUsers.rejected, getRecentSearch.rejected), (state, action) => {
      state.list = [];
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something Went Wrong";
    });

    builder.addMatcher(isAnyOf(getProfileById.pending), (state, action) => {
      state.item = null;
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addMatcher(isAnyOf(getProfileById.rejected, searchMoreUsers.rejected, getRecentSearch.rejected), (state, action) => {
      state.item = null;
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something Went Wrong";
    });
    builder.addMatcher(isAnyOf(getPublicProfileByUsername.fulfilled), (state, action) => {
      state.item = action.payload;
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
    });

    builder.addMatcher(isAnyOf(getPublicProfileByUsername.pending), (state, action) => {
      state.item = null;
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addMatcher(isAnyOf(getPublicProfileByUsername.rejected), (state, action) => {
      state.item = null;
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something Went Wrong";
    });
    builder.addMatcher(isAnyOf(updateCoverImage.pending, updateAvatar.pending), (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addMatcher(isAnyOf(updateCoverImage.fulfilled, updateAvatar.fulfilled), (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
    });
    builder.addMatcher(isAnyOf(updateCoverImage.rejected, updateAvatar.rejected), (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  editProfile,
  resetList,
  setCurrentCategory,
  clearLAEData,
  updateToDefaultAvatar,
  updateDefaultBankAccount,
  removeBankAccount,
  updatePayoutFrequency,
  updateUserReaction,
  updateUserSubscription,
  userHasExpiredDiscover,
  updateUserSettings,
  userHasRejectedDiscover,
  updateCreatorHasDiscovers,
  updateBillingAddressInfos,
  deleteUserCurrentUserEvent,
  updateOtherUserEventPayment,
  clearOtherUserInfoData,
} = usersSlice.actions;

export default usersSlice.reducer;
